import { useSavedReplies, useSendMessage } from '@zuruflex/api-client/react';
import { toLocale } from '@zuruflex/i18n';
import type { MessageThreadDetail } from '@zuruflex/types';
import { Badge, EmptyState, MessageComposer, MessageList } from '@zuruflex/ui';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';

export interface ConversationPaneProps {
  thread: MessageThreadDetail;
}

/**
 * The middle pane: the transcript and the box you answer in.
 *
 * Deliberately the same `MessageList` and `MessageComposer` the partner
 * portal and the public site render — the desk is not a second chat stack.
 * What is added here is what only staff have: the internal note toggle and
 * the canned replies.
 *
 * Nothing in here touches the stream. `useThread` above it is what listens,
 * reconnects and closes gaps; a pane that held its own cursor would be the
 * fourth implementation of a rule that exists once on purpose.
 */
export function ConversationPane({ thread }: ConversationPaneProps) {
  const { t } = useTranslation(['admin', 'common']);
  const send = useSendMessage(api, thread.id);

  /*
   * Asked for in the thread's own language rather than the agent's: what
   * matters is what the customer reads. Not narrowed by topic — the picker
   * ranks by it, and an agent whose thread is filed under ACCOUNT still
   * needs the greeting that is filed under nothing.
   */
  const savedReplies = useSavedReplies(api, { locale: toLocale(thread.locale) });

  return (
    <section className="flex min-h-0 flex-col overflow-hidden rounded-card border border-border-default bg-surface">
      <header className="flex items-center justify-between gap-3 border-b border-border-default px-4 py-3">
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold text-text">{thread.with.name}</h2>
          <p className="truncate text-xs text-text-muted">
            {thread.reference}
            {thread.with.phone ? <span> · {thread.with.phone}</span> : null}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/*
            Which door the customer came in by, because it changes what an
            agent can do: a WhatsApp customer can be rung on that number,
            and a web-chat guest may have no number at all and is reading
            the reply in a widget that closes with the tab. Only for a
            thread the concierge handed over: `channel` defaults to WEB, so
            a support thread opened from the site's own inbox reads WEB
            too, and "Web chat" on it would be a door nobody used.
          */}
          {thread.kind === 'SUPPORT' && thread.viaConcierge && thread.channel !== 'EMAIL' ? (
            <Badge variant="outline">{t(`inbox.channel.${thread.channel}`)}</Badge>
          ) : null}
          {thread.priority !== 'NORMAL' ? (
            <Badge variant={thread.priority === 'LOW' ? 'outline' : 'warning'}>
              {t(`inbox.priority.${thread.priority}`)}
            </Badge>
          ) : null}
          <Badge variant={thread.status === 'OPEN' ? 'default' : 'outline'}>
            {t(`inbox.status.${thread.status}`)}
          </Badge>
        </div>
      </header>

      <MessageList
        messages={thread.messages}
        readBySeq={thread.readBySeq}
        resolveAttachment={(fileId) => api.attachmentLink(fileId).then((link) => link.url)}
        empty={<EmptyState title={t('common:messaging.noMessages')} />}
      />

      <MessageComposer
        // Only on a support thread. A note on somebody else's conversation
        // would be an agent writing into a place its two participants can
        // see nothing of and everything around.
        allowNotes={thread.kind === 'SUPPORT'}
        masked={thread.masksContactDetails}
        pending={send.isPending}
        savedReplies={
          thread.kind === 'SUPPORT'
            ? {
                items: savedReplies.data?.items ?? [],
                labels: {
                  trigger: t('inbox.savedReplies'),
                  empty: t('inbox.savedReplyEmpty'),
                },
                locale: thread.locale,
                topic: thread.topic,
              }
            : undefined
        }
        onAttach={(file) => api.uploadThreadAttachment(thread.id, file)}
        onSend={async (body, { isPublic, attachments }) => {
          await send.mutateAsync({
            body,
            isPublic,
            attachments,
            idempotencyKey: crypto.randomUUID(),
          });
        }}
      />
    </section>
  );
}
